import React, { useEffect, useState } from 'react';
import { fetchPublishedBlog } from '../services/blogApi';
import { resolveMediaUrl } from '../utils/mediaUrl';

import { contentToBlocks } from '../utils/blogContent';

function BlogContentPageNew({ articleId, onBack, showView }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const loadArticle = async () => {
      setLoading(true);
      setError('');
      try {
        const foundArticle = await fetchPublishedBlog(articleId);
        const blocks = contentToBlocks(foundArticle.content || '');
        setArticle({ ...foundArticle, blocks });
      } catch (err) {
        setError(err.message || 'Article not found');
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    if (articleId) {
      loadArticle();
    }
  }, [articleId]);

  if (loading) {
    return <div className="text-white p-10">Loading article...</div>;
  }

  if (error || !article) {
    return <div className="text-white p-10">{error || 'Article not found'}</div>;
  }

  return (
    <div id="blog-content-view" className="view-section min-h-screen py-32 bg-black transition-all duration-500">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <button
          type="button"
          onClick={onBack}
          className="group flex min-h-[44px] items-center space-x-3 py-3 text-[10px] bank-gothic text-[#c49b66] mb-12 hover:text-white transition-colors touch-manipulation"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="group-hover:-translate-x-1 transition-transform">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          <span>Back to all articles</span>
        </button>

        <article className="text-neutral-300">
          <h1 className="bank-gothic text-3xl md:text-5xl text-white mb-6 leading-tight">{article.title}</h1>
          <p className="text-neutral-500 mb-10 text-[10px] bank-gothic tracking-widest">
            Published {article.date} — By {article.author}
          </p>

          <div className="aspect-[16/9] w-full overflow-hidden mb-12 border border-white/5">
            <img src={resolveMediaUrl(article.image)} className="w-full h-full object-cover" alt={article.title} />
          </div>

          <div className="space-y-6 text-sm md:text-base leading-relaxed font-light text-neutral-300">
            {(() => {
              let firstParagraphRendered = false;

              return article.blocks.map((block, index) => {
                if (block.type === 'heading') {
                  return (
                    <h3 key={index} className="bank-gothic text-xl md:text-2xl text-white mt-10 mb-4 tracking-wide">
                      {block.text}
                    </h3>
                  );
                }

                const showDropCap = !firstParagraphRendered;
                firstParagraphRendered = true;

                return (
                  <p key={index}>
                    {showDropCap && (
                      <span className="text-5xl font-semibold accent-text float-left mr-3 leading-none font-serif">
                        {block.text.charAt(0)}
                      </span>
                    )}
                    {showDropCap ? block.text.substring(1) : block.text}
                  </p>
                );
              });
            })()}

            {article.quote && (
              <blockquote className="border-l border-[#c49b66] pl-6 my-10 italic text-neutral-400 font-light text-lg">
                "{article.quote}"
              </blockquote>
            )}
          </div>

          {article.cta && (
            <div className="mt-16 p-8 md:p-12 border border-[#c49b66]/30 bg-gradient-to-br from-[#c49b66]/5 to-transparent">
              <h3 className="bank-gothic text-xl md:text-2xl text-white mb-4">
                {article.cta.title || "Let's Build Together"}
              </h3>
              <p className="text-neutral-300 text-sm md:text-base leading-relaxed mb-6 font-light">
                {article.cta.description}
              </p>
              <button
                onClick={() => showView && showView('contact_page')}
                className="inline-flex items-center space-x-3 bg-[#c49b66] hover:bg-[#b38a56] text-black px-8 py-4 bank-gothic text-xs tracking-widest transition-all duration-300 cursor-pointer"
              >
                <span>{article.cta.buttonText || 'CONTACT US'}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            </div>
          )}
        </article>
      </div>
    </div>
  );
}

export default BlogContentPageNew;
